import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Users, Crown, MessageSquare, Inbox, LogOut, Search, ShieldCheck, Trash2, CheckCircle2, XCircle, Image as ImageIcon, Settings, Clock, Flag, UserX } from "lucide-react";
import { toast } from "sonner";
import ProfileTypeBadge from "@/components/ProfileTypeBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [users, setUsers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [plans, setPlans] = useState([]);
  const [messages, setMessages] = useState([]);
  const [reports, setReports] = useState([]);
  const [reportTab, setReportTab] = useState("PENDING");
  const [q, setQ] = useState("");
  const [profileTypeFilter, setProfileTypeFilter] = useState("");
  const [tab, setTab] = useState("payments");
  const [paymentTab, setPaymentTab] = useState("PENDING");
  const [viewScreenshot, setViewScreenshot] = useState(null);
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [editPlan, setEditPlan] = useState(null);
  const [planForm, setPlanForm] = useState({});

  const loadStats = useCallback(async () => {
    try { const s = await api.get("/admin/stats"); setStats(s.data); } catch (e) {
      if (e.response?.status === 403) { toast.error("Admin only"); navigate("/dashboard"); }
    }
  }, [navigate]);

  const loadPayments = useCallback(async () => {
    try {
      const r = await api.get("/admin/payments", { params: { status: paymentTab } });
      setPayments(r.data.payments || []);
    } catch (_e) {}
  }, [paymentTab]);

  const loadReports = useCallback(async () => {
    try {
      const r = await api.get("/admin/reports", { params: { status: reportTab } });
      setReports(r.data.reports || []);
    } catch (_e) {}
  }, [reportTab]);

  const loadAll = useCallback(async () => {
    loadStats();
    try {
      const [u, pl, m] = await Promise.all([
        api.get("/admin/users"),
        api.get("/admin/plans"),
        api.get("/admin/messages", { params: { limit: 50 } }),
      ]);
      setUsers(u.data.users || []);
      setPlans(pl.data.plans || []);
      setMessages(m.data.messages || []);
    } catch (_e) {}
    loadPayments();
  }, [loadStats, loadPayments]);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => { loadPayments(); }, [loadPayments]);
  useEffect(() => { loadReports(); }, [loadReports]);

  const searchUsers = async () => {
    const params = {};
    if (q) params.q = q;
    if (profileTypeFilter) params.profile_type = profileTypeFilter;
    const r = await api.get("/admin/users", { params });
    setUsers(r.data.users || []);
  };

  const verifyPayment = async (id) => {
    try {
      await api.post(`/admin/payments/${id}/verify`);
      toast.success("Payment verified — subscription active");
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const confirmReject = async () => {
    if (!rejectModal) return;
    try {
      await api.post(`/admin/payments/${rejectModal.payment_id}/reject`, { reason: rejectReason || "No reason given" });
      toast.success("Payment rejected");
      setRejectModal(null);
      setRejectReason("");
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm("Delete this user and all their data?")) return;
    try { await api.delete(`/admin/users/${userId}`); toast.success("Deleted"); loadAll(); }
    catch (_e) { toast.error("Failed"); }
  };

  const actionReport = async (reportId, action, note = null) => {
    try {
      await api.post(`/admin/reports/${reportId}/action`, { action, note });
      toast.success(
        action === "DELETE_USER" ? "User deleted" :
        action === "RESOLVE" ? "Report resolved" :
        "Report dismissed"
      );
      loadReports();
      loadStats();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const openPlanEdit = (p) => {
    setEditPlan(p);
    setPlanForm({ price: p.price, billing_cycle: p.billing_cycle, duration_days: p.duration_days, description: p.description, is_active: p.is_active });
  };
  const savePlan = async () => {
    try {
      await api.put(`/admin/plans/${editPlan.plan_id}`, {
        ...planForm,
        price: parseFloat(planForm.price),
        duration_days: parseInt(planForm.duration_days, 10),
      });
      toast.success("Plan updated");
      setEditPlan(null);
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (user && user.role !== "admin") {
    return (
      <div className="admin-theme flex items-center justify-center min-h-screen">
        <div className="text-center">
          <ShieldCheck className="w-10 h-10 mx-auto text-rose-400" />
          <p className="mt-3">Admin access required.</p>
          <Button onClick={() => navigate("/dashboard")} className="mt-4 bg-teal-600 hover:bg-teal-700 rounded-full">Go to app</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-theme">
      <header className="border-b border-slate-800 bg-slate-950/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <img src="/brand/mylivefriend-logo.png" alt="MyLiveFriend" className="h-10 w-auto" draggable="false" />
            <div className="hidden sm:block">
              <div className="font-heading font-bold text-lg leading-none">Admin Console</div>
              <div className="text-[11px] text-slate-500 font-mono">COMMAND DASHBOARD · {user?.email}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button data-testid="admin-goto-app-btn" variant="ghost" onClick={() => navigate("/dashboard")} className="text-slate-300 hover:bg-slate-800 hover:text-white rounded-full">User App</Button>
            <Button data-testid="admin-logout-btn" variant="ghost" onClick={logout} className="text-rose-400 hover:bg-rose-500/10 hover:text-rose-300 rounded-full">
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <h1 className="font-heading text-3xl font-extrabold tracking-tight">Overview</h1>

        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          <KPI icon={Users} label="Total Users" value={stats.total_users ?? "—"} color="emerald" testid="admin-kpi-total-users" />
          <KPI icon={Crown} label="Active Subs" value={stats.active_subscriptions ?? "—"} color="amber" testid="admin-kpi-active-subs" />
          <KPI icon={Clock} label="Pending Payments" value={stats.pending_payments ?? "—"} color="rose" testid="admin-kpi-pending-payments" />
          <KPI icon={CheckCircle2} label="Verified Payments" value={stats.verified_payments ?? "—"} color="teal" testid="admin-kpi-verified-payments" />
          <KPI icon={XCircle} label="Rejected Payments" value={stats.rejected_payments ?? "—"} color="rose" testid="admin-kpi-rejected-payments" />
          <KPI icon={Clock} label="Expired Subs" value={stats.expired_subscriptions ?? "—"} color="indigo" testid="admin-kpi-expired-subs" />
          <KPI icon={MessageSquare} label="Total Messages" value={stats.total_messages ?? "—"} color="teal" testid="admin-kpi-messages" />
          <KPI icon={Inbox} label="Plans" value={plans.length || "—"} color="amber" testid="admin-kpi-plans" />
        </div>

        <div className="mt-10 flex gap-1 border-b border-slate-800 flex-wrap">
          {[
            { id: "payments", label: `Payment Verification ${stats.pending_payments ? `(${stats.pending_payments})` : ""}` },
            { id: "reports", label: `Reports ${stats.pending_reports ? `(${stats.pending_reports})` : ""}` },
            { id: "users", label: "Users" },
            { id: "plans", label: "Plans" },
            { id: "messages", label: "Messages" },
          ].map((t) => (
            <button
              data-testid={`admin-tab-${t.id}`}
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${tab === t.id ? "border-emerald-500 text-emerald-400" : "border-transparent text-slate-400 hover:text-slate-200"}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "payments" && (
          <section className="mt-6 admin-card rounded-xl">
            <div className="p-4 border-b border-slate-800 flex gap-2">
              {["PENDING", "VERIFIED", "REJECTED"].map((s) => (
                <button
                  key={s}
                  data-testid={`admin-payments-tab-${s}`}
                  onClick={() => setPaymentTab(s)}
                  className={`px-3 py-1 rounded-full text-xs font-semibold ${paymentTab === s ? (s === "PENDING" ? "bg-amber-500/20 text-amber-300" : s === "VERIFIED" ? "bg-emerald-500/20 text-emerald-300" : "bg-rose-500/20 text-rose-300") : "text-slate-400 hover:text-slate-200"}`}
                >
                  {s}
                </button>
              ))}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-payments-table">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-widest text-slate-500 border-b border-slate-800">
                    <th className="p-3">User</th>
                    <th className="p-3">Plan / Amount</th>
                    <th className="p-3">UTR</th>
                    <th className="p-3">Screenshot</th>
                    <th className="p-3">Submitted</th>
                    <th className="p-3">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((p) => (
                    <tr key={p.payment_id} className="border-b border-slate-800/60" data-testid={`payment-row-${p.payment_id}`}>
                      <td className="p-3">
                        <div className="font-semibold">{p.user?.name || "—"}</div>
                        <div className="text-xs text-slate-400">{p.user?.email}</div>
                        <div className="text-[11px] text-slate-500">{p.user?.city} · PIN {p.user?.pincode}</div>
                      </td>
                      <td className="p-3">
                        <div className="font-semibold">{p.plan_name}</div>
                        <div className="text-sm text-emerald-400">₹{p.amount}</div>
                      </td>
                      <td className="p-3 font-mono text-xs">{p.utr_number}</td>
                      <td className="p-3">
                        <button
                          data-testid={`view-screenshot-${p.payment_id}`}
                          onClick={() => setViewScreenshot(p)}
                          className="inline-flex items-center gap-1 text-teal-400 hover:text-teal-300 text-xs"
                        >
                          <ImageIcon className="w-4 h-4" /> View
                        </button>
                      </td>
                      <td className="p-3 text-xs text-slate-400 font-mono">{new Date(p.created_at).toLocaleString()}</td>
                      <td className="p-3">
                        {p.status === "PENDING" ? (
                          <div className="flex gap-2">
                            <Button data-testid={`verify-${p.payment_id}`} onClick={() => verifyPayment(p.payment_id)} className="h-7 px-3 bg-emerald-500 hover:bg-emerald-600 text-slate-950 rounded-full text-xs">
                              <CheckCircle2 className="w-3 h-3 mr-1" /> Verify
                            </Button>
                            <Button data-testid={`reject-${p.payment_id}`} onClick={() => { setRejectModal(p); setRejectReason(""); }} variant="ghost" className="h-7 px-3 text-rose-400 hover:bg-rose-500/10 rounded-full text-xs">
                              <XCircle className="w-3 h-3 mr-1" /> Reject
                            </Button>
                          </div>
                        ) : (
                          <div className="text-xs">
                            <div className={`font-semibold ${p.status === "VERIFIED" ? "text-emerald-400" : "text-rose-400"}`}>{p.status}</div>
                            {p.verified_by && <div className="text-slate-500 text-[11px]">by {p.verified_by}</div>}
                            {p.rejection_reason && <div className="text-rose-400/70 text-[11px] mt-1">{p.rejection_reason}</div>}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                  {payments.length === 0 && (
                    <tr><td colSpan={6} className="p-6 text-center text-slate-500">No {paymentTab.toLowerCase()} payments.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {tab === "users" && (
          <section className="mt-6 admin-card rounded-xl">
            <div className="p-4 border-b border-slate-800 flex items-center gap-2 flex-wrap">
              <Search className="w-4 h-4 text-slate-500" />
              <Input data-testid="admin-search-input" placeholder="Search by name, email, city, pincode..." value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && searchUsers()} className="bg-slate-900 border-slate-700 text-slate-100 placeholder:text-slate-500 flex-1 min-w-[180px]" />
              <select
                data-testid="admin-profile-type-filter"
                value={profileTypeFilter}
                onChange={(e) => setProfileTypeFilter(e.target.value)}
                className="bg-slate-900 border border-slate-700 rounded-md px-3 h-10 text-sm text-slate-100"
              >
                <option value="">All profile types</option>
                <option value="AVAILABLE_FOR_RENTAL">Available for Rental</option>
                <option value="LOOKING_TO_RENT">Looking to Rent</option>
              </select>
              <Button data-testid="admin-search-btn" onClick={searchUsers} className="bg-emerald-500 hover:bg-emerald-600 text-slate-950 rounded-full">Apply</Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-users-table">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-widest text-slate-500 border-b border-slate-800">
                    <th className="p-3">User</th><th className="p-3">Location</th><th className="p-3">Looking For</th><th className="p-3">Role</th><th className="p-3">Subscription</th><th className="p-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.user_id} className="border-b border-slate-800/60 hover:bg-slate-800/30">
                      <td className="p-3"><div className="font-semibold">{u.name || "—"}</div><div className="text-xs text-slate-400">{u.email}</div></td>
                      <td className="p-3 text-xs"><div>{u.city || "—"} · {u.area || "—"}</div><div className="font-mono text-slate-400">PIN {u.pincode || "—"}</div></td>
                      <td className="p-3"><ProfileTypeBadge type={u.profile_type} /></td>
                      <td className="p-3"><span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${u.role === "admin" ? "bg-rose-500/20 text-rose-300" : "bg-slate-700 text-slate-300"}`}>{u.role}</span></td>
                      <td className="p-3 text-xs">
                        <div className="font-semibold">{u.subscription_tier}</div>
                        <div className={u.subscription_active ? "text-emerald-400" : "text-slate-500"}>{u.subscription_active ? "ACTIVE" : "INACTIVE"}</div>
                      </td>
                      <td className="p-3">
                        {u.role !== "admin" && <button data-testid={`admin-delete-${u.user_id}`} onClick={() => deleteUser(u.user_id)} className="text-rose-400 hover:text-rose-300"><Trash2 className="w-4 h-4" /></button>}
                      </td>
                    </tr>
                  ))}
                  {users.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-slate-500">No users found.</td></tr>}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {tab === "plans" && (
          <section className="mt-6 grid md:grid-cols-3 gap-4">
            {plans.map((p) => (
              <div key={p.plan_id} className="admin-card rounded-xl p-5" data-testid={`admin-plan-${p.plan_id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-heading font-bold text-xl">{p.name}</div>
                    <div className="font-mono text-xs text-slate-500 uppercase">{p.plan_id}</div>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${p.is_active ? "bg-emerald-500/20 text-emerald-300" : "bg-slate-700 text-slate-400"}`}>{p.is_active ? "ACTIVE" : "DISABLED"}</span>
                </div>
                <div className="mt-3 font-heading text-3xl font-extrabold text-emerald-400">₹{p.price}</div>
                <div className="text-xs text-slate-400 mt-1">{p.duration_days} days · {p.billing_cycle}</div>
                <p className="mt-3 text-sm text-slate-400">{p.description}</p>
                <Button data-testid={`edit-plan-${p.plan_id}`} onClick={() => openPlanEdit(p)} className="mt-4 bg-slate-800 hover:bg-slate-700 text-slate-100 rounded-full w-full">
                  <Settings className="w-4 h-4 mr-2" /> Edit
                </Button>
              </div>
            ))}
          </section>
        )}

        {tab === "messages" && (
          <section className="mt-6 admin-card rounded-xl p-4">
            <h3 className="font-heading font-bold mb-3">Recent messages</h3>
            <div className="space-y-2 max-h-[600px] overflow-y-auto chat-scroll">
              {messages.map((m) => (
                <div key={m.message_id} className="p-3 bg-slate-900/60 rounded-lg border border-slate-800 text-sm">
                  <div className="flex justify-between text-xs text-slate-500 font-mono">
                    <span>{m.from_user_id} → {m.to_user_id}</span>
                    <span>{new Date(m.created_at).toLocaleString()}</span>
                  </div>
                  <div className="mt-1 text-slate-200">{m.text}</div>
                </div>
              ))}
              {messages.length === 0 && <div className="text-slate-500 text-sm text-center py-10">No messages yet.</div>}
            </div>
          </section>
        )}

        {tab === "reports" && (
          <section className="mt-6 admin-card rounded-xl">
            <div className="p-4 border-b border-slate-800 flex gap-2">
              {["PENDING", "RESOLVED", "DISMISSED", "USER_DELETED"].map((s) => (
                <button
                  key={s}
                  data-testid={`admin-reports-tab-${s}`}
                  onClick={() => setReportTab(s)}
                  className={`px-3 py-1 rounded-full text-xs font-semibold ${reportTab === s ? (s === "PENDING" ? "bg-amber-500/20 text-amber-300" : s === "RESOLVED" ? "bg-emerald-500/20 text-emerald-300" : "bg-rose-500/20 text-rose-300") : "text-slate-400 hover:text-slate-200"}`}
                >
                  {s.replace("_", " ")}
                </button>
              ))}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-reports-table">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-widest text-slate-500 border-b border-slate-800">
                    <th className="p-3">Reported User</th>
                    <th className="p-3">Reporter</th>
                    <th className="p-3">Reason</th>
                    <th className="p-3">Submitted</th>
                    <th className="p-3">Status / Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {reports.map((r) => (
                    <tr key={r.report_id} className="border-b border-slate-800/60" data-testid={`report-row-${r.report_id}`}>
                      <td className="p-3">
                        <div className="font-semibold flex items-center gap-2">
                          {r.reported?.name || r.reported_name || "(deleted)"}
                          {r.other_reports_count > 1 && (
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-rose-500/20 text-rose-300 font-mono">
                              ×{r.other_reports_count}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-slate-400">{r.reported?.email || "—"}</div>
                        <div className="text-[11px] text-slate-500">{r.reported?.city || "—"} · PIN {r.reported?.pincode || "—"}</div>
                      </td>
                      <td className="p-3">
                        <div className="font-semibold">{r.reporter?.name || r.reporter_name || "—"}</div>
                        <div className="text-xs text-slate-400">{r.reporter?.email || "—"}</div>
                      </td>
                      <td className="p-3">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 text-xs font-semibold">
                          <Flag className="w-3 h-3" /> {r.reason}
                        </span>
                      </td>
                      <td className="p-3 text-xs text-slate-400 font-mono">{new Date(r.created_at).toLocaleString()}</td>
                      <td className="p-3">
                        {r.status === "PENDING" ? (
                          <div className="flex flex-wrap gap-2">
                            <Button data-testid={`report-resolve-${r.report_id}`} onClick={() => actionReport(r.report_id, "RESOLVE")} className="h-7 px-3 bg-emerald-500 hover:bg-emerald-600 text-slate-950 rounded-full text-xs">
                              <CheckCircle2 className="w-3 h-3 mr-1" /> Resolve
                            </Button>
                            <Button data-testid={`report-dismiss-${r.report_id}`} onClick={() => actionReport(r.report_id, "DISMISS")} variant="ghost" className="h-7 px-3 text-slate-300 hover:bg-slate-700/40 rounded-full text-xs">
                              <XCircle className="w-3 h-3 mr-1" /> Dismiss
                            </Button>
                            {r.reported?.role !== "admin" && (
                              <Button
                                data-testid={`report-delete-user-${r.report_id}`}
                                onClick={() => {
                                  if (window.confirm(`Delete ${r.reported?.name || "this user"} permanently? This cannot be undone.`)) {
                                    actionReport(r.report_id, "DELETE_USER");
                                  }
                                }}
                                variant="ghost"
                                className="h-7 px-3 text-rose-400 hover:bg-rose-500/10 rounded-full text-xs"
                              >
                                <UserX className="w-3 h-3 mr-1" /> Delete user
                              </Button>
                            )}
                          </div>
                        ) : (
                          <div className="text-xs">
                            <div className={`font-semibold ${r.status === "RESOLVED" ? "text-emerald-400" : r.status === "USER_DELETED" ? "text-rose-400" : "text-slate-400"}`}>{r.status.replace("_", " ")}</div>
                            {r.resolved_by && <div className="text-slate-500 text-[11px]">by {r.resolved_by}</div>}
                            {r.resolution_note && <div className="text-slate-400/70 text-[11px] mt-1">{r.resolution_note}</div>}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                  {reports.length === 0 && (
                    <tr><td colSpan={5} className="p-6 text-center text-slate-500">No {reportTab.toLowerCase().replace("_", " ")} reports.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </main>

      {/* View screenshot modal */}
      <Dialog open={!!viewScreenshot} onOpenChange={() => setViewScreenshot(null)}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 max-w-2xl">
          <DialogHeader>
            <DialogTitle>Payment Screenshot · UTR {viewScreenshot?.utr_number}</DialogTitle>
          </DialogHeader>
          {viewScreenshot && (
            <div className="mt-2">
              <img
                data-testid="screenshot-image"
                src={`${BACKEND}/api/payment/screenshot/${viewScreenshot.payment_id}`}
                alt="Payment screenshot"
                className="w-full rounded-lg border border-slate-700"
              />
              <div className="mt-3 text-sm text-slate-400">
                <div>User: {viewScreenshot.user?.name} ({viewScreenshot.user?.email})</div>
                <div>Plan: {viewScreenshot.plan_name} · ₹{viewScreenshot.amount}</div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reject reason modal */}
      <Dialog open={!!rejectModal} onOpenChange={(o) => !o && setRejectModal(null)}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 max-w-md">
          <DialogHeader><DialogTitle>Reject Payment</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-400">Give a short reason — the user will see this.</p>
          <Textarea data-testid="reject-reason-input" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="e.g. Amount mismatch, screenshot unclear..." className="bg-slate-800 border-slate-700 text-slate-100" rows={3} />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRejectModal(null)} className="text-slate-300">Cancel</Button>
            <Button data-testid="confirm-reject-btn" onClick={confirmReject} className="bg-rose-500 hover:bg-rose-600 text-white rounded-full">Reject payment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit plan modal */}
      <Dialog open={!!editPlan} onOpenChange={(o) => !o && setEditPlan(null)}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 max-w-md">
          <DialogHeader><DialogTitle>Edit {editPlan?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500">Price (₹)</label>
              <Input data-testid="plan-price-input" type="number" value={planForm.price ?? ""} onChange={(e) => setPlanForm({ ...planForm, price: e.target.value })} className="bg-slate-800 border-slate-700" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500">Billing cycle</label>
              <Input data-testid="plan-cycle-input" value={planForm.billing_cycle ?? ""} onChange={(e) => setPlanForm({ ...planForm, billing_cycle: e.target.value })} placeholder="monthly / quarterly / yearly" className="bg-slate-800 border-slate-700" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500">Duration (days)</label>
              <Input data-testid="plan-duration-input" type="number" value={planForm.duration_days ?? ""} onChange={(e) => setPlanForm({ ...planForm, duration_days: e.target.value })} className="bg-slate-800 border-slate-700" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500">Description</label>
              <Textarea data-testid="plan-desc-input" value={planForm.description ?? ""} onChange={(e) => setPlanForm({ ...planForm, description: e.target.value })} className="bg-slate-800 border-slate-700" rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditPlan(null)} className="text-slate-300">Cancel</Button>
            <Button data-testid="save-plan-btn" onClick={savePlan} className="bg-emerald-500 hover:bg-emerald-600 text-slate-950 rounded-full">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KPI({ icon: Icon, label, value, color, testid }) {
  const colorMap = {
    emerald: "text-emerald-400 bg-emerald-500/10",
    amber: "text-amber-400 bg-amber-500/10",
    teal: "text-teal-400 bg-teal-500/10",
    rose: "text-rose-400 bg-rose-500/10",
    indigo: "text-indigo-400 bg-indigo-500/10",
  };
  return (
    <div className="admin-card rounded-xl p-5" data-testid={testid}>
      <div className={`inline-flex p-2 rounded-lg ${colorMap[color]}`}><Icon className="w-5 h-5" /></div>
      <div className="mt-3 text-3xl font-heading font-extrabold">{value}</div>
      <div className="text-xs uppercase tracking-widest text-slate-500 mt-1">{label}</div>
    </div>
  );
}
